import React, { useCallback, useEffect, useRef, useState } from "react";
import CryptoHuntHomeOptions from "../../components/games/CryptoHuntHomeOptions";
import CryptoHuntHomeIntro from "../../components/games/CryptoHuntHomeIntro";
import HintContainer from "../../components/games/HintContainer";
import CryptoHuntGameWrapper from "../../components/games/CryptoHuntGameWrapper";
import { IoCalendarClearOutline } from "react-icons/io5";
import { SiMarketo } from "react-icons/si";
import { TbCategory2 } from "react-icons/tb";
import { IoSettingsOutline } from "react-icons/io5";
import { IoIosGlobe } from "react-icons/io";
import { LuUtilityPole } from "react-icons/lu";
import { TbExchange } from "react-icons/tb";
import { IoUnlinkSharp } from "react-icons/io5";
import { toast } from "react-toastify";
import CryptoHuntFailedCard from "../../components/games/CryptoHuntFailedCard";
import {
  decryptData,
  handleTransactionError,
  toastPromise,
} from "../../services/util";
import { checkGuess, getCryptoHuntWord, getSignature } from "../../api/games";
import { useQueryClient } from "@tanstack/react-query";
import CryptoHuntWinCard from "../../components/games/CryptoHuntWinCard";
import { useWallet } from "../../hooks/useWallet";
import { useAuth } from "../../hooks/useAuth";
import { parseEther } from "ethers/lib/utils";
import FullPageLoader from "../../components/Loader/FullPageLoader";
import { ethers } from "ethers";

const CryptoHunt = () => {
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [finalPoints, setFinalPoints] = useState(0);
  const [win, setWin] = useState(false);
  const [gameData, setGameData] = useState({
    gameId: "",
    guessWord: "",
    hintUsed: 0,
    wordLength: 0,
  });
  const [guessResponse, setGuessResponse] = useState({
    data: {
      correct: false,
      feedback: [],
      correctWord: "",
      triesLeft: 0,
    },
    isLoading: false,
  });
  const [time, setTime] = useState(90);
  const [startReady, setStartReady] = useState(false);
  const [prevGuessWords, setPrevGuessWords] = useState([]);
  const [letterFeedback, setLetterFeedback] = useState([]);
  const [userInput, setUserInput] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalHuntReward, setTotalHuntReward] = useState(0);
  const [attempt, setAttempt] = useState(5);
  const [dailyLeft, setDailyLeft] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [wordComplete, setWordComplete] = useState(false);
  const [keyPressed, setKeyPressed] = useState(false);
  const inputRef = useRef([]);
  const timeTakenRef = useRef(0);
  const wallet = useWallet();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [hintUsed, setHintUsed] = useState([]);
  const [penalty, setPenalty] = useState(0);
  const [attemptsDone, setAttemptsDone] = useState(0);

  const [hints, setHints] = useState([
    {
      _id: 1,
      title: "Categories",
      penalty: 0,
      icon: TbCategory2,
      value: "",
    },

    {
      _id: 2,
      title: "Total Supply",
      penalty: 0,
      icon: IoUnlinkSharp,
      value: "",
    },
    {
      _id: 3,
      title: "Market Cap",
      penalty: 200,
      icon: SiMarketo,
      value: "",
    },
    {
      _id: 4,
      title: "ATH",
      penalty: 50,
      icon: IoSettingsOutline,
      value: "",
    },

    {
      _id: 5,
      title: "ATH Date",
      penalty: 50,
      icon: IoCalendarClearOutline,
      value: "",
    },

    {
      _id: 6,
      title: "ATL",
      penalty: 50,
      icon: TbExchange,
      value: "",
    },

    {
      _id: 7,
      title: "Platforms",
      penalty: 50,
      icon: IoIosGlobe,
      value: "",
    },

    {
      _id: 8,
      title: "Max Supply",
      penalty: 50,
      icon: LuUtilityPole,
      value: "",
    },
  ]);
  useEffect(() => {
    const allFieldsFilled = userInput.every((char) => char !== "");
    if (allFieldsFilled) {
      setWordComplete(true);
    }
  }, [userInput]);

  useEffect(() => {
    let intervalId;
    if (isGameStarted) {
      intervalId = setInterval(() => {
        setTime((prevTime) => {
          if (win) {
            clearInterval(intervalId);
            return 0;
          } else if (attempt < 1) {
            clearInterval(intervalId);
            setIsFailed(true);
            return 0;
          } else if (prevTime <= 1) {
            setWin(false);
            setIsFailed(true);
            clearInterval(intervalId);

            return 0;
          } else {
            timeTakenRef.current = prevTime;
            return prevTime - 1;
          }
        });
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [attempt, win, time, isGameStarted, isFailed]);

  useEffect(() => {
    if (isFailed && time === 0) {
      handleCheckGuess(
        gameData?.gameId,
        Math.random().toString().substring(0, gameData?.wordLength)
      );
    }
  }, [isFailed, time]);
  const getGameData = useCallback(async () => {
    setIsLoading(true);
    setStartReady(false);
    const data = await queryClient.fetchQuery({
      queryKey: ["crypto Hunt"],
      queryFn: () => getCryptoHuntWord(),
      staleTime: 0,
      cacheTime: 0,
    });
    if (data) {
      setAttempt(data?.maxTries);
      setGameData((x) => {
        return {
          ...x,
          gameId: data?.gameId,
          hintUsed: 0,
          wordLength: data?.wordLength,
        };
      });
      setUserInput(Array(data?.wordLength).fill(""));
      const decryptedHints = {
        marketCap: decryptData(data?.hints?.marketCap),
        ath: decryptData(data?.hints?.ath),
        athDate: decryptData(data?.hints?.athDate),
        atl: decryptData(data?.hints?.atl),
        platforms: decryptData(data?.hints?.platforms),
        maxSupply: decryptData(data?.hints?.maxSupply),
        categories: data?.hints?.categories,
        totalSupply: data?.hints?.totalSupply,
      };
      setHints((prevHints) =>
        prevHints.map((hint) => {
          const keyMapping = {
            Categories: decryptedHints.categories,
            "Total Supply": decryptedHints.totalSupply,
            "Market Cap": decryptedHints.marketCap,
            ATH: decryptedHints.ath,
            "ATH Date": decryptedHints.athDate,
            ATL: decryptedHints.atl,
            Platforms: decryptedHints.platforms,
            "Max Supply": decryptedHints.maxSupply,
          };
          return {
            ...hint,
            value: keyMapping[hint.title] || hint.value,
          };
        })
      );

      setIsError(isError);
      setStartReady(true);
      setIsLoading(false);
    }
  }, [queryClient]);
  const handleCheckGuess = useCallback(
    async (gameId, guessWord, hintsUsed) => {
      setGuessResponse((prev) => {
        return {
          data: {
            ...prev.data,
            feedback: [],
          },
          isLoading: true,
        };
      });
      try {
        const data = await queryClient.fetchQuery({
          queryKey: ["checkGuess", gameId, guessWord, hintsUsed],
          queryFn: () => checkGuess(gameId, guessWord, hintsUsed),
          staleTime: 0,
        });
        setGuessResponse({
          data: data,
          isLoading: false,
        });
        setWin(data?.correct);
        if (data?.correct) {
          setDailyLeft((x) => x - 1);
          setAttemptsDone((x) => x + 1);
        }

        setLetterFeedback((x) => [...x, data?.feedback]);
      } catch (error) {
        console.error(error);
      }
    },
    [queryClient]
  );
  const handleClickPenalty = (penalty, id) => {
    setHintUsed((prevHintUsed) => {
      if (!prevHintUsed.includes(id) && penalty !== 0) {
        const updatedHintUsed = [...prevHintUsed, id];
        setPenalty((prevPenalty) => prevPenalty + penalty);
        return updatedHintUsed;
      }
      return prevHintUsed;
    });
  };
  const handleChange = (value, index) => {
    const updatedInput = [...userInput];
    updatedInput[index] = value.slice(0, 1)?.toUpperCase();
    setUserInput(updatedInput);
    if (value && index < gameData?.wordLength - 1) {
      inputRef.current[index + 1]?.focus();
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      const allFieldsFilled = userInput.every((char) => char !== "");
      if (allFieldsFilled) {
        if (inputRef.current.length > 0) {
          inputRef.current[0]?.focus();
        }
        setAttempt((x) => x - 1);
        setKeyPressed(true);
        setPrevGuessWords((x) => [...x, userInput.join("")]);
        setUserInput(Array(gameData?.wordLength).fill(""));
        setWordComplete(false);
        handleCheckGuess(
          gameData?.gameId,
          userInput?.join("")?.toLowerCase(),
          penalty
        );
      } else {
        toast.error("Please fill all the character!");
      }
    }
  };
  const handleClose = async () => {
    setWin(false);
    setIsFailed(false);
    setIsGameStarted(false);
    setStartReady(false);
    setFinalPoints(0);
    setAttemptsDone(0);
    await init();
  };
  const handleStartButton = () => {
    if (dailyLeft < 1) {
      toast.error("Come back tomorrow!");
      return 0;
    }
    setTime(90);
    setGameData({
      gameId: "",
      guessWord: "",
      hintUsed: 0,
      wordLength: 0,
    });
    setGuessResponse({
      data: {
        correct: false,
        feedback: [],
        correctWord: "",
        triesLeft: 0,
      },
      isLoading: false,
    });
    setAttempt(5);
    setPenalty(0);
    setHintUsed([]);
    setIsFailed(false);
    setWin(false);
    setStartReady(false);
    setPrevGuessWords([]);
    setUserInput(Array(gameData?.wordLength).fill(""));
    setIsGameStarted(true);
    setLetterFeedback([]);
    getGameData();
  };
  const handleTryNext = () => {
    handleStartButton();
  };
  const handlePlayNext = async () => {
    const result = await guessResponse?.data?.pointsEarned;
    if (result) {
      const updatedFinalPoints = finalPoints + result;
      setFinalPoints(updatedFinalPoints);
    }
    handleStartButton();
  };

  const handleMoveHome = () => {
    setIsGameStarted(false);
    setStartReady(false);
    setFinalPoints(0);
    setWin(false);
    setIsFailed(false);
    setPenalty(0);
  };
  const handleSubmit = async () => {
    const result = await guessResponse?.data?.pointsEarned;
    const updatedFinalPoints = finalPoints + result;
    setFinalPoints(updatedFinalPoints);
    const config = wallet.getChainConfig();
    if (!config) return;
    setIsSubmitting(true);
    const transactionOptions = {
      value: parseEther(
        String((config.features.gameValue * 2 * attemptsDone).toFixed(8))
      ),
      ...(config.features.gasLimit && { gasLimit: "900000" }),
    };
    const message = "Crypto Hunt";
    const nonce = 168;
    const signature = await getSignature({
      to: wallet.address,
      amount: ethers.utils.parseEther(updatedFinalPoints.toString()),
      message: message,
      nonce: nonce,
      chainId: wallet.chainId,
    });
    const [, tx] = await wallet.safeCallContract({
      name: "Game",
      method: "disperseToken",
      args: [
        "Hunt",
        attemptsDone,
        Number(localStorage.getItem("multiplier") || 1),
        ethers.utils.parseEther(updatedFinalPoints.toString()),
        message,
        nonce,
        signature,
        transactionOptions,
      ],
    });

    if (tx) {
      await toastPromise(tx.wait(), {
        pending: "Submitting...",
        success: () => {
          setFinalPoints(0);
          setAttemptsDone(0);
          setTotalHuntReward(
            +totalHuntReward +
              Number(updatedFinalPoints) *
                Number(localStorage.getItem("multiplier") || 1)
          );
          wallet.updatePoints({
            amount:
              +updatedFinalPoints *
              Number(localStorage.getItem("multiplier") || 1),
            chainId: wallet.chainId,
            walletAddress: wallet.address,
            moduleName: "hunt",
            txHash: tx.hash,
          });
          auth.updateTokens();
          return "Submitted successfully!";
        },
        error: (err) => {
          handleTransactionError(err);
        },
      });
    }
    setIsSubmitting(false);
    handleMoveHome();
  };
  const init = async () => {
    const [, left] = await wallet.safeCallContract({
      name: "Game",
      method: "getTotalTriesLeft",
      args: [wallet?.address, "Hunt"],
    });
    if (left) {
      setDailyLeft(left?.toNumber());
    }
    const [, rewards] = await wallet.safeCallContract({
      name: "Game",
      method: "getTotalTokensEarned",
      args: [wallet?.address, "Hunt"],
    });

    if (rewards) {
      setTotalHuntReward(
        Number(ethers.utils.formatUnits(rewards.toString()).toString()).toFixed(
          0
        )
      );
    }
  };

  useEffect(() => {
    if (!wallet.address) return;
    init();
  }, [wallet.address, wallet.chainId]);
  if (isLoading) return <FullPageLoader />;

  return (
    <div
      className={`min-h-screen xs:px-5  overflow-hidden 2xl:container w-full relative ${
        isGameStarted
          ? "sm:px-10 xs:px-10 px-4"
          : "2xl:px-12 lg:px-16 md:px-10 "
      }  mx-auto md:pt-28 sm:pt-24 pt-20`}
    >
      <h1
        className={`${
          startReady && isGameStarted && "lg:left-20 "
        } xl:text-6xl lg:text-4xl   lg:w-fit w-full  relative xs:text-3xl text-2xl lg:text-left text-center font-bold font-akira`}
      >
        Crypto Hunt
      </h1>

      <div
        className={`flex lg:flex-row flex-col lg:gap-y-0 gap-y-12   2xl:mt-10 mt-4 justify-between  ${
          isGameStarted ? "gap-x-0" : "2xl:gap-x-24 lg:gap-x-16"
        } lg:items-start items-center w-full`}
      >
        <div
          className={`flex justify-center  ${
            isGameStarted && "lg:order-1 order-2"
          }  items-center w-full`}
        >
          <CryptoHuntHomeIntro isActive={!isGameStarted || isLoading} />
          <CryptoHuntGameWrapper
            letterFeedback={letterFeedback}
            guessResponse={guessResponse}
            isWin={win}
            isActive={startReady}
            attemptsLeft={attempt}
            handleChange={handleChange}
            userInput={userInput}
            inputRef={inputRef}
            handleKeyPress={handleKeyPress}
            isWordCompleted={wordComplete}
            prevWords={prevGuessWords}
            time={time}
            dailyLeft={dailyLeft}
          />
        </div>
        <div
          className={`flex w-full  h-full ${
            isGameStarted && "lg:order-2 order-1 "
          }`}
        >
          <CryptoHuntHomeOptions
            currentReward={finalPoints}
            totalReward={totalHuntReward}
            dailyLeft={dailyLeft}
            isActive={!isGameStarted}
            handleStartButton={handleStartButton}
          />

          <>
            {((startReady && !win && !isFailed) || isLoading) && (
              <HintContainer
                hints={hints}
                penalty={penalty}
                handleClickHint={handleClickPenalty}
                hintsUsed={hintUsed}
              />
            )}
            {win && !isFailed && (
              <CryptoHuntWinCard
                totalAttempt={5 - attempt}
                timeTaken={90 - timeTakenRef.current}
                wordTries={prevGuessWords}
                totalPoints={2000}
                isActive={win}
                handleClose={handleClose}
                penalty={penalty}
                guessResponse={guessResponse}
                handleSubmit={handleSubmit}
                handleTryNext={handlePlayNext}
                dailyLeft={dailyLeft}
                isSubmitting={isSubmitting}
              />
            )}
            {isFailed && !win && (attempt < 1 || time === 0) && (
              <CryptoHuntFailedCard
                isActive={isFailed}
                totalAttempt={5 - attempt}
                timeTaken={90 - timeTakenRef.current}
                wordTries={prevGuessWords}
                totalPoints={2000}
                correctWord={guessResponse?.data?.correctWord}
                handleTryNext={handleTryNext}
                handleClose={handleClose}
              />
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default CryptoHunt;
